/**
 * IAM (Identity and Access Management) Service
 * Handles trust levels and authorization
 *
 * Note: This service does NOT manage user profile data (name, phone, email) in storage.
 * Callers should use UserContext to manage profile data, which automatically syncs to storage.
 */

import { UserLogic } from '@/modules/user';
import type { User } from '@/modules/user';
import { ClaimsLogic } from '@/modules/idv/logic';
import type { Claims } from '@/modules/idv/types';
import { apiCall } from '@/util/api';

/**
 * Revoke trust level and switch to unverified
 * This function revokes both phone and email verification by calling DELETE endpoints for each.
 * The backend gets the user ID from the JWT token - no user ID is sent in the request body.
 * It preserves the previous user ID if available from the existing token.
 *
 * @returns User with unverified trust level (or null if guest)
 * @throws Error if trust level revocation fails
 */
export async function deletePhoneAndEmailVerifications(): Promise<User | null> {
  // Revoke both phone and email verifications
  // We call both endpoints, but we only need the response from the last one
  // since both will result in unverified trust level
  const phoneResponse = await apiCall('/api/idv/verifications/phone', {
    method: 'DELETE',
  });

  if (!phoneResponse.ok) {
    const errorText = await phoneResponse.text();
    throw new Error(`Failed to revoke phone verification: ${errorText}`);
  }

  const emailResponse = await apiCall('/api/idv/verifications/email', {
    method: 'DELETE',
  });

  if (!emailResponse.ok) {
    const errorText = await emailResponse.text();
    throw new Error(`Failed to revoke email verification: ${errorText}`);
  }

  const data = await emailResponse.json();

  if (!data.success) {
    throw new Error('Failed to revoke trust level');
  }

  // Unmarshal user from backend response
  const user = UserLogic.fromNestedUserJSON(data);

  // Token is stored automatically by apiCall via applyTokenFromResponse

  return user;
}

/**
 * Revoke only the verifications in `claims` that have lapsed, so the backend
 * reissues a token carrying whichever claim is still live. A session's token
 * expires with its earliest claim; revoking both there would also discard a
 * phone or email verification this device still holds.
 */
export async function revokeLapsedVerifications(claims: Claims): Promise<void> {
  const lapsed = [
    !ClaimsLogic.isPhoneVerified(claims) && 'phone',
    !ClaimsLogic.isEmailVerified(claims) && 'email',
  ].filter(Boolean);

  for (const level of lapsed) {
    const response = await apiCall(`/api/idv/verifications/${level}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to revoke ${level} verification: ${errorText}`);
    }
  }
}

/**
 * Identify with a pending invite.
 *
 * When a guest follows a `/b/{legId}` invite link, we exchange their session for
 * a fresh guest token that carries a `PendingInvite` claim for that leg. This
 * marks the guest as the leg's pending invitee so a by-invitation-only action can
 * offer them an "unlock by linking your phone" path. No user id is sent — this stays
 * a guest token until the user actually links a phone.
 *
 * Best-effort: failures are swallowed so a transient error never blocks routing to
 * the action view (the capability simply won't be granted).
 */
export async function identifyWithPendingInvite(legId: string): Promise<void> {
  try {
    await apiCall('/api/auth/identify', {
      method: 'POST',
      body: JSON.stringify({ legId }),
    });
  } catch {
    // Swallow — routing should proceed regardless.
  }
}

/**
 * Revoke email IDV and preserve phone verification
 * This function revokes only email verification while preserving phone verification.
 * The backend gets the user ID from the JWT token - no user ID is sent in the request body.
 * It generates a new JWT token with updated trust level (phone verified, email unverified).
 *
 * @returns User with updated trust level (phone verified, email revoked), or null if guest
 * @throws Error if email IDV revocation fails
 */
export async function deleteEmailVerification(): Promise<User | null> {
  const response = await apiCall('/api/idv/verifications/email', {
    method: 'DELETE',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to revoke email IDV: ${errorText}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error('Failed to revoke email IDV');
  }

  // Unmarshal user from backend response
  const user = UserLogic.fromNestedUserJSON(data);

  // Token is stored automatically by apiCall via applyTokenFromResponse

  return user;
}
